/**
 * v1.3.16 — Manual Airplane Mode toggle.
 *
 * Header-strip switch that lets the user put the app into an
 * "offline-but-not-really" state where the device stays on the
 * network but every AI/network-heavy action queues locally
 * instead of firing live. Draining resumes on the user's next
 * explicit toggle-off.
 *
 * Renders NOTHING when the feature flag is off, so this file is
 * a zero-visibility no-op in production tenants that haven't
 * opted in via REACT_APP_ENABLE_MANUAL_AIRPLANE_MODE.
 *
 * Coexists with the existing offline indicators (WifiOff chip,
 * OfflineExistingAssetIndicator). It never removes or replaces
 * them — it augments.
 */
import React from 'react';
import { Plane } from 'lucide-react';
import { toast } from 'sonner';
import { useConnectivity } from '../lib/connectivity';

// v1.3.17 — Safety guards.
//   AUTO_OFF_MS: If a user leaves the toggle on for more than 12
//     hours (typically because they forgot), auto-clear it on the
//     next component mount. Prevents "why isn't anything syncing?"
//     the next morning.
//   BATTERY_LOW_THRESHOLD: We warn — but do not block — if the
//     device battery is under this level when the user turns the
//     toggle on, because the eventual drain (multiple AI jobs
//     replaying at once) is a heavier hit than staying online.
const AUTO_OFF_MS = 12 * 60 * 60 * 1000;  // 12 hours
const AUTO_OFF_TS_KEY = 'fmiq:manualAirplaneMode:enabledAt';
const BATTERY_LOW_THRESHOLD = 0.20;

export const AirplaneModeToggle = ({ collapsed = false, variant = 'dark', queuedCount = 0 }) => {
  const {
    featureEnabled,
    manualAirplane,
    networkOnline,
    setManualAirplaneMode,
  } = useConnectivity();

  // v1.3.17 — Auto-off timer. If the toggle was left on for more
  // than AUTO_OFF_MS, flip it off on next mount and inform the user.
  // This has to run BEFORE the render guard so a stale enable
  // still gets cleaned up even when the feature flag is toggled off
  // between sessions.
  React.useEffect(() => {
    if (!featureEnabled || !manualAirplane) return;
    let enabledAt;
    try {
      enabledAt = parseInt(window.localStorage.getItem(AUTO_OFF_TS_KEY) || '0', 10);
    } catch (_) { enabledAt = 0; }
    // If we don't have a timestamp yet, stamp now (recovers a state
    // set by an older release before the timestamp key existed).
    if (!enabledAt) {
      try { window.localStorage.setItem(AUTO_OFF_TS_KEY, Date.now().toString()); } catch (_) {}
      return;
    }
    const age = Date.now() - enabledAt;
    if (age > AUTO_OFF_MS) {
      setManualAirplaneMode(false);
      try { window.localStorage.removeItem(AUTO_OFF_TS_KEY); } catch (_) {}
      toast.info(
        `Focus Mode auto-cleared after ${Math.round(age / 3_600_000)} hours idle. Draining queued work now.`,
        { duration: 6000 },
      );
    }
    // Intentionally empty deps — only fire on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!featureEnabled) return null;

  const handleToggle = async () => {
    const next = !manualAirplane;
    // v1.3.17 — On enable, check battery level and warn (but do not
    // block) if it's low. The eventual drain replay hits the battery
    // harder than staying online, so a heads-up is warranted.
    if (next && typeof navigator !== 'undefined' && navigator.getBattery) {
      try {
        const battery = await navigator.getBattery();
        if (battery && !battery.charging && battery.level < BATTERY_LOW_THRESHOLD) {
          toast.warning(
            `Battery is at ${Math.round(battery.level * 100)}% — the eventual drain of queued AI tasks may drain it further. Consider charging first.`,
            { duration: 6000 },
          );
        }
      } catch (_) {
        // Battery API unavailable / rejected — silently skip; the
        // toggle should still work in browsers without it.
      }
    }
    // Record / clear the enable timestamp for the auto-off timer.
    try {
      if (next) window.localStorage.setItem(AUTO_OFF_TS_KEY, Date.now().toString());
      else window.localStorage.removeItem(AUTO_OFF_TS_KEY);
    } catch (_) {}

    setManualAirplaneMode(next);
    if (next) {
      // v1.3.35.35 — vendors never see the word "AI" (their whole
      // surface hides AI processing by design). Same toggle, same
      // behaviour; different copy per caller variant.
      const onCopy = variant === 'light'
        ? 'Focus Mode ON — your captures will queue and upload when you turn it off.'
        : 'Focus Mode ON — AI + network tasks will queue and run when you turn it off.';
      toast.info(onCopy, {
        duration: 4000,
      });
    } else {
      const offCopy = variant === 'light'
        ? 'Focus Mode OFF — uploading your captures.'
        : 'Focus Mode OFF — draining queued work.';
      toast.success(offCopy, {
        duration: 3000,
      });
      // The OfflineContext auto-sync effect will pick this up on
      // the next render tick because `effectiveOnline` just flipped
      // to true (assuming the network is up). We don't need to
      // trigger drain manually here — that keeps a single drain
      // path.
    }
  };

  const active = manualAirplane;

  // v1.3.35.34 — Light variant. The dark palette below is tuned
  // for the sidebar rail (dark navy background). The vendor pages
  // have a white top bar, so render a slate-on-white pill instead.
  // Same behaviour (toggle, storage, event); pure visual switch.
  if (variant === 'light') {
    return (
      <button
        type="button"
        onClick={handleToggle}
        data-testid="airplane-mode-toggle-light"
        aria-pressed={active}
        title={
          active
            ? 'Focus Mode is ON. Photos queue locally and drain when you turn it off.'
            : 'Focus Mode — queue photos locally and upload later. Tap to enable.'
        }
        className={
          'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border transition-colors text-xs font-medium ' +
          (active
            ? 'bg-sky-50 border-sky-300 text-sky-800 hover:bg-sky-100'
            : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50 hover:text-slate-900')
        }
      >
        <Plane size={12} className={'flex-shrink-0 ' + (active ? 'rotate-45' : '')} />
        <span className="hidden sm:inline">
          {active
            ? (networkOnline ? 'Focus On' : 'Focus + No Signal')
            : 'Focus Mode'}
        </span>
        {/* v1.3.35.36 — Queue depth badge. Only render when Focus
            Mode is ON AND at least one capture is waiting to
            drain, so a vendor knows exactly how many photos will
            upload when they toggle off. */}
        {active && queuedCount > 0 && (
          <span
            data-testid="focus-mode-queue-badge"
            className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-sky-900 bg-sky-200 rounded-full px-1.5 py-[1px] ml-0.5"
          >
            · {queuedCount} queued
          </span>
        )}
        <span
          className={
            'inline-block w-6 h-3 rounded-full relative transition-colors ml-0.5 ' +
            (active ? 'bg-sky-500' : 'bg-slate-300')
          }
        >
          <span
            className={
              'absolute top-0.5 w-2 h-2 rounded-full bg-white transition-transform ' +
              (active ? 'translate-x-3.5' : 'translate-x-0.5')
            }
          />
        </span>
      </button>
    );
  }

  // Collapsed rail: icon-only pill.
  if (collapsed) {
    return (
      <button
        type="button"
        onClick={handleToggle}
        data-testid="airplane-mode-toggle-collapsed"
        aria-pressed={active}
        title={active ? 'Focus Mode ON — tap to drain' : 'Focus Mode OFF — tap to enable (in-app airplane mode)'}
        className={
          'mb-2 mx-auto flex items-center justify-center h-8 w-8 rounded-md transition-colors ' +
          (active
            ? 'bg-sky-500/20 border border-sky-400/40 text-sky-200'
            : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200')
        }
      >
        <Plane size={14} className={active ? 'rotate-45' : ''} />
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleToggle}
      data-testid="airplane-mode-toggle"
      aria-pressed={active}
      title={
        active
          ? 'Focus Mode is ON. Tap to resume live network + AI.'
          : 'In-app airplane mode — queue AI + network work to run later. Tap to enable.'
      }
      className={
        'mb-2 w-full flex items-center gap-1.5 px-2 py-1 rounded transition-colors text-left ' +
        (active
          ? 'bg-sky-500/20 border border-sky-400/40 text-sky-100'
          : 'text-slate-400 hover:bg-slate-800/70 hover:text-slate-200 border border-transparent')
      }
    >
      <Plane size={12} className={'flex-shrink-0 ' + (active ? 'rotate-45' : '')} />
      <span className="text-[11px] font-medium tracking-tight flex-1 min-w-0 truncate">
        {active
          ? (networkOnline ? 'Focus Mode ON' : 'Focus + No Network')
          : 'Focus Mode'}
      </span>
      <span
        className={
          'inline-block w-6 h-3 rounded-full relative transition-colors ' +
          (active ? 'bg-sky-400' : 'bg-slate-600')
        }
      >
        <span
          className={
            'absolute top-0.5 w-2 h-2 rounded-full bg-white transition-transform ' +
            (active ? 'translate-x-3.5' : 'translate-x-0.5')
          }
        />
      </span>
    </button>
  );
};

export default AirplaneModeToggle;
